import {ActivityCarBusState, WaypointsProps} from "../../types-enums/activity-car-bus";
import {i18n} from "i18next";
import {GeocodingFeature} from "@mapbox/search-js-core/dist/geocode/types";
import {BUS_CATEGORY, CAR_CATEGORY, VEHICLE_TYPE} from "../../api/model/activity-car-bus";
import {
    CAR_BUS_CALCULATION_HINT,
    COMMA_SEPARATOR,
    conditionallyToLowerCase,
    formatDistanceKmString,
    generateDayString,
    generateFuelConsumptionString,
    generatePassengerString,
    generateStringListWithAndString,
    ROUTE_SEPARATOR,
    WhatStringToInsert,
    WhereToDisplayActivityString
} from "./common";
import {getCalculatedOrManualEnteredDistanceKm} from "../getter/car-bus";

/**
 * Generates a string containing customer car/bus-related information with metadata.
 *
 * @param {ActivityCarBusState} carBus - The state object containing car/bus activity data.
 * @param {i18n} i18n - Internationalization object used for localized translations.
 */
export const generateCustomerCarBusInformationString = (
    carBus: ActivityCarBusState,
    i18n: i18n,
    withRoute: boolean,
) => {
    const {tierNumber} = generateCarBusCalculationTierHintString(carBus, i18n)
    const showFootnote = tierNumber >= 2
    return {...generateCarBusSpecificMetaDataString(carBus, false, showFootnote, i18n, withRoute), showFootnote}
}

/**
 * Generates a formatted string containing professional car or bus activity information.
 *
 * @param {ActivityCarBusState} carBus - The activity state of the car or bus, containing metadata about the vehicle.
 * @param {i18n} i18n - An internationalization object used for localizing strings.
 * @param {boolean} withRoute - A flag indicating whether to include route-related details in the generated string.
 * @param {WhereToDisplayActivityString} [whereToDisplay] - Optional parameter specifying where the generated string will be displayed, used for contextual formatting.
 */
export const generateProfessionalCarBusInformationString = (
    carBus: ActivityCarBusState,
    i18n: i18n,
    withRoute: boolean,
    whereToDisplay?: WhereToDisplayActivityString
) => generateCarBusSpecificMetaDataString(carBus, true, false, i18n, withRoute, whereToDisplay)

/**
 * Generates a metadata string specific to car and bus activities based on the provided parameters.

 * @param {ActivityCarBusState} carBus - Object representing the current state of the car or bus activity.
 * @param {boolean} showAcrissCode - Determines whether the ACRISS code should be included in the metadata string.
 * @param {boolean} showFootnote - Determines whether a footnote should be appended to the metadata string.
 * @param {i18n} i18n - Internationalization object used for translations and language resolution.
 * @param {boolean} withRoute - Indicates whether a route string derived from waypoints should be included.
 * @param {WhereToDisplayActivityString} [whereToDisplay] - Specifies the context where the metadata string will be displayed. Supported values are travelComponents, pdf, journey, and dropdownJourney.
 * @returns {{ carBusString: string, separator: string }} - An object containing the generated metadata string (`carBusString`) and the separator (`separator`) used in the string.
 *                                                        -> Car (SUV/Jeep), Electric (23 kw/h), 87,345 km
 */
const generateCarBusSpecificMetaDataString = (
    carBus: ActivityCarBusState,
    showAcrissCode: boolean,
    showFootnote: boolean,
    i18n: i18n,
    withRoute: boolean,
    whereToDisplay?: WhereToDisplayActivityString,
): { carBusString: string, separator: string } => {
    const {t, resolvedLanguage} = i18n
    const {
        vehicleType,
        fuelType,
        manualEnteredDistanceKm,
        calculatedDistanceKm,
        fuelConsumption,
        carCategory,
        busCategory,
        acrissCode,
        passengerCount,
        countDays
    } = carBus
    let metaDataString = ''
    let fuelConsumptionAdded = false
    let route: string | null = null

    let vehicleTypeString: string | null = null
    if (vehicleType) vehicleTypeString = generateCarBusString(vehicleType, i18n) ?? null
    if (vehicleType === VEHICLE_TYPE.bus) {
        const metaDataStringWithPassengerCount = `${passengerCount} ${generatePassengerString(passengerCount, i18n)}`
        if (busCategory) {
            metaDataString = `${vehicleTypeString} (${generateTranslatedBusCategoryString(i18n, busCategory)}), ${metaDataStringWithPassengerCount}`
        } else {
            metaDataString = `${vehicleTypeString}, ${metaDataStringWithPassengerCount}`
        }
    }

    if (vehicleType === VEHICLE_TYPE.car) {
        if (carCategory) {
            metaDataString = `${vehicleTypeString} (${generateTranslatedCarCategoryString(i18n, carCategory)})`
        } else {
            if (vehicleTypeString !== null) metaDataString = vehicleTypeString
        }
    }
    if (showAcrissCode && acrissCode) metaDataString = `${metaDataString} (${acrissCode})`
    if (fuelType !== null) {
        metaDataString = `${metaDataString}, ${t(`ACTIVITIES.CAR_BUS.FUEL_TYPE.${fuelType}`)}`
        if (fuelConsumption !== null) {
            metaDataString = `${metaDataString} (${generateFuelConsumptionString(fuelType, fuelConsumption, resolvedLanguage)})`
            fuelConsumptionAdded = true
        }
    }

    if (fuelConsumption !== null && !fuelConsumptionAdded) metaDataString = `${metaDataString}, ${generateFuelConsumptionString(fuelType, fuelConsumption, resolvedLanguage)}`
    if (calculatedDistanceKm !== null) metaDataString = `${metaDataString}, ${formatDistanceKmString(calculatedDistanceKm, resolvedLanguage)}`
    if (withRoute) {
        route = generateCarBusRouteFromWaypointsString(carBus.waypoints, i18n, true)
        if (route !== null) metaDataString = `${metaDataString}, ${route}`
    }
    if (manualEnteredDistanceKm !== null) metaDataString = `${metaDataString}, ${formatDistanceKmString(manualEnteredDistanceKm, resolvedLanguage)}`
    if (typeof whereToDisplay !== 'undefined') switch (whereToDisplay) {
        case WhereToDisplayActivityString.travelComponents:
            metaDataString = `${metaDataString}, ${generateDayString(countDays, i18n)}`
            break;
        case WhereToDisplayActivityString.pdf:
            break;
        case WhereToDisplayActivityString.journey:
            break;
        case WhereToDisplayActivityString.dropdownJourney:
            break;
        default:
            whereToDisplay satisfies never
    }
    if (showFootnote) metaDataString = `${metaDataString}${CAR_BUS_CALCULATION_HINT}`
    return {carBusString: metaDataString, separator: COMMA_SEPARATOR}
}

/**
 * Generates a formatted route string for car or bus transportation from a list of waypoints.
 *
 * @param {WaypointsProps} waypoints - An array of waypoints, where each waypoint can contain properties such as context, place, street, or full_address.
 * @param {i18n} i18n - The internationalization object containing translation functions.
 * @param {boolean} shortRoute - A boolean flag indicating whether to generate a short representation of the route.
 * @returns {string | null} - A formatted string representing the route, or null if the waypoints are invalid or empty.
 *                          -> Manchester: Rockdale Road --- No address found, using coordinates (-74.34493765148285, 44.12501516524597) --- No address found, using coordinates (-71.38008365046403, 43.051597260255335) --- Providence: Murray Street
 */
export const generateCarBusRouteFromWaypointsString = (waypoints: WaypointsProps, i18n: i18n, shortRoute: boolean) => {
    const {t} = i18n
    if (waypoints === null) return null
    if (waypoints.every(waypoint => waypoint === null)) return null
    return waypoints.map((waypoint, index) => {
        let previousWaypoint: GeocodingFeature | null = null
        if (waypoint && waypoint.properties) {
            if (waypoint.properties.context) {
                const {properties: {context: {place, street}}} = waypoint
                if (index === 0 || index === waypoints.length - 1) {
                    if (shortRoute) {
                        if (place) return place.name
                    } else {

                        if (place && street) return `${place.name}: ${street.name}`
                        if (place) return `${place.name}`
                        if (street) return `${t("ACTIVITIES.CAR_BUS.NO_CITY")}: ${street.name}`
                    }
                }
                if (previousWaypoint) {
                    const {context: {place: previousPlace}} = waypoint?.properties
                    if (shortRoute) {
                        if (previousPlace !== place && place) return place.name
                    } else {
                        if (previousPlace !== place) return `${place}: ${street}`
                        return `${street}`
                    }
                }
            } else {
                const {properties: {full_address}} = waypoint
                return full_address
            }
        } else {
            return 'GENERAL.ERROR'
        }
        previousWaypoint = waypoint
    })
        .filter(Boolean)
        .join(ROUTE_SEPARATOR)
}

/**
 * Generates a translated string for a given vehicle type (car or bus).
 *
 * @param {VEHICLE_TYPE} vehicleType - The type of the vehicle, must be of enum type VEHICLE_TYPE.
 * @param {i18n} i18n - The internationalization object used for translation.
 * @returns {string | null} The translated string representing the vehicle type, or null if no matching type exists.
 *
 * @throws {Error} If the vehicleType does not match any of the defined cases.
 */
export const generateCarBusString = (vehicleType: VEHICLE_TYPE, i18n: i18n) => {
    const {t,} = i18n
    let translatedString: string | null = null
    switch (vehicleType) {
        case VEHICLE_TYPE.bus:
            translatedString = t('ACTIVITIES.CAR_BUS.BUS')
            break;
        case VEHICLE_TYPE.car:
            translatedString = t('ACTIVITIES.CAR_BUS.CAR')
            break
        default:
            vehicleType satisfies never;
    }
    return translatedString
}

/**
 * Generates a localized string describing the passenger count for a given vehicle type.
 *
 * @param {VEHICLE_TYPE} vehicleType - The type of vehicle (e.g., car, bus).
 * @param {number} passengerCount - The number of passengers to include in the string.
 * @param {i18n} i18n - The internationalization instance containing translation functions.
 * @returns {string} - A localized string indicating the vehicle type and passenger count.
 *                   -> Bus for 10 Passengers
 */
export const generateCarBusPassengerCountString = (
    vehicleType: VEHICLE_TYPE,
    passengerCount: number,
    i18n: i18n
) => {
    const {t} = i18n
    return `${generateCarBusString(vehicleType, i18n)} ${t("GENERAL.FOR")} ${passengerCount} ${generatePassengerString(passengerCount, i18n)}`
}


/**
 * Generates a tier hint string for car and bus emission calculations based on the provided car or bus activity state and internationalization references.
 * The number of days driven is used to estimate the distance traveled per day. Fuel consumption is derived from a standard value for the specified vehicle type. You can calculate the emissions more precisely by adding further information such as distance, fuel consumption or Acriss code.
 *
 * @param {ActivityCarBusState} carBus - The car or bus activity state containing relevant information such as vehicle type, category, fuel consumption, and distances.
 * @param {i18n} i18n - The internationalization object providing translation methods for localized string generation.
 * @returns {{tierMessage: string, improvements: string, tierNumber: number}} An object containing the tier-related message, improvement suggestions, and the tier number.
 *
 * The function analyzes the available data from the car or bus activity state to determine which calculation tier is applicable.
 * Depending on the presence of certain fields, it selects the most suitable emission calculation hints and indications for potential improvements.
 *
 * - If the fuel consumption is present, Tier 1 is applied with the highest accuracy.
 * - If the distances are not available, Tier 2 is applied with derived fuel consumption and default distances along with improvement suggestions.
 * - Otherwise, Tier 3 is applied, using derived fuel consumption and distances when possible, with additional improvement suggestions.
 *
 * The method references translation keys to generate localized hint messages and lists fields that could improve the accuracy of calculations.
 */
export const generateCarBusCalculationTierHintString = (carBus: ActivityCarBusState, i18n: i18n): {
    tierMessage: string,
    improvements: string,
    tierNumber: number
} => {
    const {
        acrissCode,
        manualEnteredDistanceKm,
        calculatedDistanceKm,
        fuelConsumption,
        vehicleType,
        carCategory,
        busCategory,
        countDays,
    } = carBus
    const vehicleCategory = carCategory ?? busCategory
    const distanceKm = getCalculatedOrManualEnteredDistanceKm(manualEnteredDistanceKm, calculatedDistanceKm)
    const {t} = i18n
    let tierAndImprovements: { tierMessage: string; improvements: string, tierNumber: number }
    let derivedFuelConsumptionUsed = ''
    let defaultDistanceValueUsed = ''
    if (vehicleType === VEHICLE_TYPE.bus) {
        derivedFuelConsumptionUsed = "HINTS.EMISSION_CALCULATION.CAR_BUS.FROM_BUS_TYPE"
    } else {
        if (acrissCode !== null) {
            derivedFuelConsumptionUsed = "HINTS.EMISSION_CALCULATION.CAR_BUS.FROM_ACRISS_CODE"
        } else {
            derivedFuelConsumptionUsed = "HINTS.EMISSION_CALCULATION.CAR_BUS.FROM_CAR_TYPE"
        }
    }

    if (distanceKm === null) defaultDistanceValueUsed = "HINTS.EMISSION_CALCULATION.CAR_BUS.DEFAULT_DISTANCE_VALUE_USED"
    // https://atmosfair.atlassian.net/wiki/spaces/IT/pages/938803203/4.+Car+bus+calculation+logic
    if (fuelConsumption !== null) {
        // TIER 1
        let improvements = ""
        if (vehicleCategory === null && vehicleType !== null) improvements = `${t("HINTS.EMISSION_CALCULATION.CAR_BUS.BETTER_CALCULATION_POSSIBILITIES", {
            values: `${t("LABELS.CAR_BUS.FUEL_TYPE")}`
        })}`
        tierAndImprovements = {
            tierMessage: t("HINTS.EMISSION_CALCULATION.CAR_BUS.FUEL_CONSUMPTION"),
            improvements,
            tierNumber: 1
        }
    } else if (distanceKm === null) {
        tierAndImprovements = {
            tierMessage: `${t("HINTS.EMISSION_CALCULATION.CAR_BUS.DERIVED_FUEL_CONSUMPTION_USED", {derivedFuelConsumption: t(derivedFuelConsumptionUsed)})} ${t(defaultDistanceValueUsed)}`,
            improvements: `${t("HINTS.EMISSION_CALCULATION.CAR_BUS.BETTER_CALCULATION_POSSIBILITIES", {
                interpolation: {escapeValue: false},
                values: generateStringListWithAndString([
                    conditionallyToLowerCase(t("GENERAL.FUEL_CONSUMPTION"), i18n),
                    conditionallyToLowerCase(t("GENERAL.DISTANCE"), i18n),
                    acrissCode && t("LABELS.CAR_BUS.ACRISS_CODE"),
                ], i18n, WhatStringToInsert.andOr)
            })}`,
            tierNumber: 2
        }
    } else if (carCategory !== null) {
        tierAndImprovements = {
            tierMessage: `${t("HINTS.EMISSION_CALCULATION.CAR_BUS.CAR_CATEGORY_DAYS_OF_USE_USED", {
                interpolation: {escapeValue: false},
                carCategory: t(`ACTIVITIES.CAR_BUS.CAR_CATEGORIES.${carCategory}`),
                days: generateDayString(countDays, i18n)
            })}`,
            improvements: `${t("HINTS.EMISSION_CALCULATION.CAR_BUS.BETTER_CALCULATION_POSSIBILITIES", {
                interpolation: {escapeValue: false},
                values: generateStringListWithAndString([
                    conditionallyToLowerCase(t("GENERAL.FUEL_CONSUMPTION"), i18n),
                    conditionallyToLowerCase(t("GENERAL.DISTANCE"), i18n),
                ], i18n, WhatStringToInsert.andOr)
            })}`,
            tierNumber: 3
        }
    } else {
        tierAndImprovements = {
            tierMessage: "",
            improvements: "",
            tierNumber: 4
        }
    }

    return tierAndImprovements
}

/**
 * Generates a localized string containing hints and information about possible improvements
 * for car and bus emission calculations. The string includes additional details or suggestions
 * based on fuel consumption and distance factors.
 *
 * @param {i18n} i18n - The internationalization object used for translations.
 * @returns {string} - A formatted and localized string that provides guidance on better
 *                   calculation possibilities for car and bus emissions.
 *                   -> ••You can calculate the emissions more precisely by adding further information such as fuel consumption and distance.
 */
export const generateCarBusCalculationImprovementsString = (i18n: i18n) => {
    const {t} = i18n
    return `${CAR_BUS_CALCULATION_HINT}${t("HINTS.EMISSION_CALCULATION.CAR_BUS.BETTER_CALCULATION_POSSIBILITIES", {
        values: generateStringListWithAndString([
            conditionallyToLowerCase(t("GENERAL.FUEL_CONSUMPTION"), i18n),
            conditionallyToLowerCase(t("GENERAL.DISTANCE"), i18n),
        ], i18n, WhatStringToInsert.andOr)
    })}`
}

/**
 * Generates a translated string for a given bus category.
 *
 * @param {Object} i18n - The internationalization object used for translations.
 * @param {string} busCategory - The bus category to be translated.
 * @returns {string} - The translated string for the specified bus category.
 *                   -> Journey coach
 */
export const generateTranslatedBusCategoryString = (i18n: i18n, busCategory: BUS_CATEGORY) => i18n.t(`ACTIVITIES.CAR_BUS.BUS_CATEGORIES.${busCategory}`)

/**
 * Generates a localized string representation for a specified car category.
 *
 * @function
 * @param {Object} i18n - The internationalization instance used for translation.
 * @param {CAR_CATEGORY} carCategory - A car category identifier used to determine the translated string.
 * @returns {string} - Translated car category string based on the provided internationalization context.
 *                   -> SUV/Jeep
 */
export const generateTranslatedCarCategoryString = (i18n: i18n, carCategory: CAR_CATEGORY) => i18n.t(`ACTIVITIES.CAR_BUS.CAR_CATEGORIES.${carCategory}`)